import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowLeft, Trash2 } from "lucide-react";
import API from "../api";
import ProductCard from "../components/ProductCard";

export default function FavoritesPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jkl_favorites") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    if (favIds.length === 0) { setLoading(false); return; }
    Promise.all(favIds.map((id) => API.get(`/api/products/${id}`).then((r) => r.data).catch(() => null)))
      .then((results) => setProducts(results.filter(Boolean)))
      .finally(() => setLoading(false));
  }, []);

  const removeFav = (id) => {
    const next = favIds.filter((f) => f !== id);
    localStorage.setItem("jkl_favorites", JSON.stringify(next));
    setFavIds(next);
    setProducts((prev) => prev.filter((p) => p._id !== id));
  };

  const clearAll = () => {
    localStorage.setItem("jkl_favorites", "[]");
    setFavIds([]);
    setProducts([]);
  };

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/catalogue" className="text-gray-400 hover:text-gray-600 transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-xl font-extrabold text-[#232F3E] flex items-center gap-2">
                <Heart size={20} className="text-red-500 fill-red-500" /> Mes Favoris
              </h1>
              <p className="text-sm text-gray-500">{products.length} produit{products.length > 1 ? "s" : ""} sauvegardé{products.length > 1 ? "s" : ""}</p>
            </div>
          </div>
          {products.length > 0 && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-300 px-3 py-1.5 rounded-full transition-colors"
              data-testid="clear-favorites-btn"
            >
              <Trash2 size={12} /> Tout supprimer
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
                <div className="aspect-square bg-gray-200" />
                <div className="p-2.5 space-y-2">
                  <div className="h-3 bg-gray-200 rounded" />
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={28} className="text-red-300" />
            </div>
            <p className="text-gray-600 font-semibold mb-1">Aucun favori pour l'instant</p>
            <p className="text-gray-400 text-sm mb-4">Ajoutez des produits en cliquant sur le ♡ sur les fiches produit</p>
            <Link to="/catalogue" className="inline-flex items-center gap-2 bg-[#FF9900] text-white font-bold px-6 py-2.5 rounded-xl text-sm hover:bg-[#E68A00] transition-colors" data-testid="browse-catalog-btn">
              Parcourir le catalogue
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {products.map((p) => (
              <div key={p._id} className="relative">
                <ProductCard product={p} />
                <button
                  onClick={() => removeFav(p._id)}
                  className="absolute top-9 right-2 z-20 w-6 h-6 bg-white rounded-full border border-gray-200 flex items-center justify-center hover:border-red-300 transition-colors"
                  title="Retirer des favoris"
                  data-testid={`remove-fav-${p._id}`}
                >
                  <Trash2 size={11} className="text-red-400" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
